/* ===========================================================================
   Nintara — Diaper Bag Tabs
   File: assets/diaper-bag-tabs.js   (loaded with `defer`)

   Owns the page-wide bag-type state, the tab price sync, and the sticky strip.

   WHAT IS *NOT* IN HERE
   The no-flash state script stays inline in the section. It has to set
   `data-bag-type` on <html> BEFORE the browser parses the sections below it,
   or the wrong bag paints for a frame. A deferred script runs after parsing,
   so it is structurally incapable of doing that job. Eight lines inline beat a
   render-blocking request.

   CONFIG comes off the section root as data attributes, since an asset file
   cannot read Liquid settings:
     data-default-type      backpack | duffle
     data-unstick           "true" | "false"
     data-label-backpack    tab label, used in the screen-reader announcement
     data-label-duffle      ditto

   MULTI-INSTANCE
   Every `[data-nbt]` root is initialised independently and registered in
   `instances`. Window-level scroll and resize listeners are attached once and
   fan out, pruning roots the theme editor has removed from the DOM.
   =========================================================================== */

(function () {
  'use strict';

  if (window.__nintaraTabsLoaded) return;
  window.__nintaraTabsLoaded = true;

  var html = document.documentElement;
  var instances = [];

    /* =========================================================
     SCROLL ANCHORING ACROSS THE TOGGLE
     Both halves of the page exist in the DOM; the toggle just
     swaps which is display:none. That reflows everything around
     a fixed scrollY, so the shopper lands somewhere arbitrary.
     Instead: note which section is at the top of the readable
     area, find its twin in the other half, and put that twin in
     the same place.
     ========================================================= */
  function bagSections(type) {
    return Array.prototype.slice.call(
      document.querySelectorAll('[data-nintara-bag="' + type + '"]')
    );
  }

  /* The Shopify wrapper carries the schema `class` — identical for
     both bag types — so it pairs a section with its twin. */
  function sectionKey(el) {
    var wrap = el.closest ? el.closest('.shopify-section') : null;
    var cls = wrap ? String(wrap.className || '') : '';
    var m = cls.match(/section-diaper-bag-[\w-]+/);
    return m ? m[0] : null;
  }

  function twinOf(el, fromType, toType) {
    var from = bagSections(fromType), to = bagSections(toType);
    var key = sectionKey(el);
    if (key) {
      var fk = from.filter(function (n) { return sectionKey(n) === key; });
      var tk = to.filter(function (n) { return sectionKey(n) === key; });
      var i = fk.indexOf(el);
      if (i > -1 && tk[i]) return tk[i];       /* same class, same position */
    }
    var j = from.indexOf(el);                   /* fallback: document order */
    return (j > -1 && to[j]) ? to[j] : null;
  }

  /* Top of the readable area: under the pinned strip, else under a
     sticky header, else the viewport top. */
  function readableTop() {
    var strip = document.querySelector('[data-nbt-strip]');
    if (strip && strip.classList.contains('is-stuck')) {
      return Math.max(0, strip.getBoundingClientRect().bottom);
    }
    var header = document.querySelector('.t4s-section-header');
    if (header) {
      var pos = getComputedStyle(header).position;
      if (pos === 'fixed' || pos === 'sticky') {
        return Math.max(0, header.getBoundingClientRect().bottom);
      }
    }
    return 0;
  }

  function captureAnchor(fromType, line) {
    var list = bagSections(fromType), chosen = null, delta = 0;
    for (var i = 0; i < list.length; i++) {
      var r = list[i].getBoundingClientRect();
      if (!r.height) continue;                  /* hidden half */
      if (r.top <= line + 1) { chosen = list[i]; delta = line - r.top; }
    }
        /* REVIEWS ONLY — the loop above picks the last section whose TOP edge has
       crossed the line, so a reviews carousel sitting centred on screen loses
       to the banner still clipped above it, and the toggle threw the reader
       back to that banner. Other sections keep the original rule: generalising
       it disturbed focus elsewhere. */
    var vh = window.innerHeight || document.documentElement.clientHeight;
    for (var j = 0; j < list.length; j++) {
      if (sectionKey(list[j]) !== 'section-diaper-bag-reviews') continue;
      var rr = list[j].getBoundingClientRect();
      if (!rr.height) continue;
      var vis = Math.min(rr.bottom, vh) - Math.max(rr.top, line);
      /* Half the section on screen, or half the readable area filled by it. */
      if (vis > rr.height * 0.5 || vis > (vh - line) * 0.5) {
        chosen = list[j];
        delta = line - rr.top;
        break;
      }
    }
    if (!chosen) return null;                   /* above the first one — leave it */
    var h = chosen.getBoundingClientRect().height;
    /* Ratio, not raw px: twins differ in height, and a raw offset can
       overshoot into the following section. */
    return { el: chosen, ratio: h ? Math.min(Math.max(delta / h, 0), 1) : 0 };
  }

  function restoreAnchor(anchor, fromType, toType, line) {
    if (!anchor) return;
    var target = twinOf(anchor.el, fromType, toType);
    if (!target) return;

    function apply() {
      var r = target.getBoundingClientRect();
      if (!r.height) return;
      var y = r.top + (anchor.ratio * r.height) - line;
      if (Math.abs(y) < 1) return;
      /* behavior:'auto' so a global scroll-behavior:smooth can't animate this. */
      window.scrollBy({ top: y, left: 0, behavior: 'auto' });
    }

    apply();
    /* Second pass once the newly shown half has finished laying out
       (lazy images, content-visibility work). Self-correcting: if the
       first pass landed it, this is a no-op. */
    window.requestAnimationFrame(apply);
  }

  /* =========================================================
     State — the single source of truth for the whole page
     ========================================================= */
  function getBagType(fallback) {
    return html.getAttribute('data-bag-type') || fallback || 'backpack';
  }

  window.NintaraLP = window.NintaraLP || {};
  window.NintaraLP.bagType = getBagType();

  document.addEventListener('nintara:bagchange', function (e) {
    if (e.detail) window.NintaraLP.bagType = e.detail.bagType;
  });

  /* =========================================================
     One instance
     ========================================================= */
  function initRoot(root) {
    if (!root || root.__nbtInit) return;
    root.__nbtInit = true;

    var strip  = root.querySelector('[data-nbt-strip]');
    var spacer = root.querySelector('[data-nbt-spacer]');
    var status = root.querySelector('[data-nbt-status]');
    var tabs   = Array.prototype.slice.call(root.querySelectorAll('[data-nbt-tab]'));
    if (!strip || !spacer) return;

    var defaultType  = root.getAttribute('data-default-type') || 'backpack';
    var unstickAtEnd = root.getAttribute('data-unstick') === 'true';
    var labels = {
      backpack: root.getAttribute('data-label-backpack') || 'Backpack',
      duffle:   root.getAttribute('data-label-duffle')   || 'Duffle Bag'
    };

    function paintTabs(type) {
      tabs.forEach(function (t) {
        t.setAttribute('aria-selected', t.getAttribute('data-nbt-tab') === type ? 'true' : 'false');
      });
    }

      function setBagType(type, opts) {
      opts = opts || {};
      if (type !== 'backpack' && type !== 'duffle') return;
      if (!opts.force && getBagType(defaultType) === type) return;

      /* Captured BEFORE the swap — the old half still has its heights.
         Skipped on the silent boot call, where there's nothing to preserve. */
      var prevType = getBagType(defaultType);
      var line = readableTop();
      var anchor = opts.silent ? null : captureAnchor(prevType, line);

      html.setAttribute('data-bag-type', type);
      paintTabs(type);

      /* Keep the URL shareable and back-button friendly. */
      if (!opts.silent && window.history && window.history.replaceState) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('bag', type);
          window.history.replaceState({}, '', url);
        } catch (err) {}
      }

      /* Everything downstream — the PDP block, the sticky ATC — listens here. */
      document.dispatchEvent(new CustomEvent('nintara:bagchange', {
        detail: { bagType: type, silent: !!opts.silent }
      }));

      /* Analytics: without this, backpack and duffle blend into one number. */
      if (!opts.silent) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'nintara_bag_toggle', bag_type: type });
        if (status) status.textContent = (labels[type] || type) + ' selected.';
      }
      restoreAnchor(anchor, prevType, type, line);
    }

    window.NintaraLP.setBagType = setBagType;

    /* ---------------------------------------------------------
       Tab price — follows the colour the PDP section is showing.
       A price typed into the customizer is locked and never
       overwritten.
       --------------------------------------------------------- */
    document.addEventListener('nintara:productchange', function (e) {
      if (!e.detail || !e.detail.bagType || !e.detail.price) return;
      var tab = root.querySelector('[data-nbt-tab="' + e.detail.bagType + '"]');
      if (!tab || tab.getAttribute('data-price-locked') === 'true') return;
      var el = tab.querySelector('[data-nbt-price]');
      if (el) el.textContent = e.detail.price;
    });

    /* ---------------------------------------------------------
       Sticky strip
       `position: sticky` releases at the section's bottom edge,
       but the design keeps the tabs pinned down the whole page.
       So the strip goes `fixed` and the spacer holds its height.
       --------------------------------------------------------- */
    var stuck = false;

    function applyStickyOffset() {
      /* The theme header is sticky at top:0 with a height driven by two
         customizer sliders, and its lower nav row is hidden below 1025px —
         so it is measured, never hardcoded. */
      var header = document.querySelector('.t4s-section-header');
      if (!header) return;
      var h = Math.round(header.getBoundingClientRect().height);
      if (h > 0) root.style.setProperty('--nbt-sticky-top', h + 'px');
    }

    function stickyTopPx() {
      var n = parseInt(getComputedStyle(root).getPropertyValue('--nbt-sticky-top'), 10);
      return isNaN(n) ? 0 : n;
    }

    function measureStrip() {
      if (stuck) return;
      spacer.setAttribute('data-h', Math.round(strip.getBoundingClientRect().height));
    }

    function onScroll() {
      var top = stickyTopPx();
      var shouldStick = spacer.getBoundingClientRect().top <= top;

      if (shouldStick && unstickAtEnd) {
        var stripH = parseInt(spacer.getAttribute('data-h'), 10) || 0;
        if (root.getBoundingClientRect().bottom <= top + stripH) shouldStick = false;
      }

      if (shouldStick === stuck) return;
      stuck = shouldStick;

      if (stuck) {
        var h = spacer.getAttribute('data-h') || Math.round(strip.getBoundingClientRect().height);
        spacer.setAttribute('data-h', h);
        spacer.style.height = h + 'px';
        strip.classList.add('is-stuck');
      } else {
        strip.classList.remove('is-stuck');
        spacer.style.height = '';
      }
    }

    /* ---------------------------------------------------------
       Interaction
       --------------------------------------------------------- */
    strip.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var tab = e.target.closest('[data-nbt-tab]');
      if (tab) setBagType(tab.getAttribute('data-nbt-tab'));
    });

    /* Arrow / Home / End move between tabs. Both keep their natural tab stop
       so keyboard users reach the unselected option and hear its state —
       deliberately not a roving tabindex. */
    strip.addEventListener('keydown', function (e) {
      if (!e.target || !e.target.closest) return;
      var tab = e.target.closest('[data-nbt-tab]');
      if (!tab) return;
      var i = tabs.indexOf(tab), next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (e.key === 'Home') next = tabs[0];
      else if (e.key === 'End') next = tabs[tabs.length - 1];
      else return;
      e.preventDefault();
      next.focus();
      setBagType(next.getAttribute('data-nbt-tab'));
    });

    /* ---------------------------------------------------------
       Boot this instance
       --------------------------------------------------------- */
    applyStickyOffset();
    measureStrip();
    /* force + silent: paints the tabs and lets sections that parsed after the
       inline state script pick up the current value. */
    setBagType(getBagType(defaultType), { force: true, silent: true });
    onScroll();

    instances.push({
      root: root,
      onScroll: onScroll,
      onResize: function () { applyStickyOffset(); measureStrip(); onScroll(); }
    });
  }

  /* =========================================================
     Window listeners — attached once, fanned out
     ========================================================= */
  var ticking = false;

  function prune() {
    instances = instances.filter(function (i) { return document.contains(i.root); });
  }

  function onScrollAll() {
    ticking = false;
    prune();
    instances.forEach(function (i) { i.onScroll(); });
  }

  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(onScrollAll);
  }, { passive: true });

  window.addEventListener('resize', function () {
    prune();
    instances.forEach(function (i) { i.onResize(); });
  }, { passive: true });

  /* =========================================================
     Init
     Deferred scripts run after parsing, so the DOM is ready —
     but the readyState check keeps this safe if the tag is ever
     moved or the file is imported some other way.
     ========================================================= */
  function initAll() {
    Array.prototype.slice.call(document.querySelectorAll('[data-nbt]')).forEach(initRoot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  /* Header height edited in the customizer, or a section re-rendered. */
  document.addEventListener('shopify:section:load', function () {
    initAll();
    prune();
    instances.forEach(function (i) { i.onResize(); });
  });
})();
